import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { colors } from 'spw-theme';
import makeStyles from '@mui/styles/makeStyles';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  TextField,
  FormControlLabel,
  Select,
  DialogProps,
  SelectProps,
  TextFieldProps,
  FormControl,
  TableHead,
  TableContainer,
  DialogActions,
  DialogActionsProps,
  TableBody,
  CheckboxProps,
  Checkbox,
  DialogContent,
  Box,
  TableCell,
  Table,
  Paper,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { omit } from 'ramda';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PaperComponentProps } from 'spw-components/dialogs/total-program/types';
import {
  GetTopLeft,
  usePositionableDialog,
} from 'spw-programming-projects/program-summary/cards/hooks/use-positionable-dialog';
import {
  decreasePositionablePSDialogCounter,
  increasePositionablePSDialogCounter,
} from 'spw-redux/modules/program-summary/programming/actions';
import { Z_INDEX_HIGHER_THAN_ALL_THE_LAYOUT } from 'spw-constants';

interface TargetYearSelectProps {
  label: string;
}

export const TotalProgramScenarioDialog = withStyles(
  (props: DialogProps) => <Dialog {...props} disableScrollLock />,
  () => {
    return {
      root: {
        '& .MuiPaper-root': {
          // [theme.breakpoints.up('lg')]: {
          //   minWidth: '54%',
          // },
          '@media (min-width:1450px)': {
            minWidth: '50%',
          },
          '@media (min-width:1720px)': {
            minWidth: '40%',
          },
          minWidth: '58%',
        },
      },
    };
  }
);

export const TotalProgramDialogTitle = styled(DialogTitle)`
  && {
    padding: 24px 24px 0px 24px;
  }
`;

export const InputLabel = styled.div`
  && {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.4px;
    margin-bottom: 4px;
  }
`;

export const ScenarioNameInput = withStyles(
  (props: TextFieldProps) => <TextField {...props} variant="outlined" />,
  {
    root: {
      height: '40px',
      width: '100%',
      '& .MuiOutlinedInput-root': {
        height: '100%',
      },
      '& .MuiOutlinedInput-input': {
        padding: '10px 16px',
        fontSize: 14,
        lineHeight: 20,
      },
    },
  }
);

export const ProgramTotalParagraph = styled.div`
  && {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    margin-top: 32px;
    margin-bottom: 24px;
  }
`;

export const RadioGroupLabel = styled(FormControlLabel)`
  && {
    margin-left: 8px;
  }

  .MuiTypography-root {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const TargetYearSelect = withStyles(
  (props: SelectProps & TargetYearSelectProps) => {
    return (
      <FormControl variant="outlined">
        <InputLabel>{props.label}</InputLabel>
        <Select
          {...(omit(['label'], props) as SelectProps & TargetYearSelectProps)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{ disableScrollLock: true }}
        />
      </FormControl>
    );
  },
  {
    root: {
      borderRadius: '4px',
      height: '40px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '14px',
      lineHeight: '20px',
      width: 140,
    },
    select: {
      padding: '10px 0px 0px 12px',
    },
  }
);

export const ScenariosTableContainer = styled(TableContainer)`
  && {
    margin-top: 18px;
  }
`;

export const ScenariosTableHeader = styled(TableHead)`
  && {
    .MuiTableCell-root {
      font-family: Roboto;
      font-style: normal;
      font-weight: 500;
      background: ${colors.offWhite};
      padding: 0px;
      font-size: 10px;
      line-height: 12px;
    }
  }
`;

export const ScenariosTableBody = styled(TableBody)`
  && {
    height: 100%;
    .MuiTableRow-root.disabled {
      .MuiTableCell-root {
        color: ${colors.mainGray};
      }
    }
    .MuiTableCell-root {
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0.25px;
      color: rgba(0, 0, 0, 0.85);
      padding: 0px;

      &.truncate {
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 0;
        overflow: hidden;
        padding-right: 8px;
      }
    }
  }
`;

export const ScenariosTruncatedContent = styled(Box)<{ disabled?: boolean }>`
  && {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 85%;
    opacity: ${(props) => (props.disabled ? 0.7 : 1.0)};
  }
`;

export const CustomCheckbox = withStyles(
  (props: CheckboxProps) => {
    return <Checkbox {...props} />;
  },
  {
    root: {
      padding: 8,
    },
  }
);

export const TotalProgramDialogActions = styled((props: DialogActionsProps) => (
  <DialogActions {...props}>{props.children}</DialogActions>
))`
  && {
    display: flex;
    justify-content: flex-start;
    padding: 16px;
  }
`;

export const LinkedScenariosText = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.25px;
  color: #000000;
`;

export const CirculationMultiplierDialog = withStyles(
  (props: DialogProps) => <Dialog {...props} disableScrollLock />,
  {
    root: {
      '& .MuiPaper-root': {
        minWidth: 684,
      },
    },
  }
);

export const DialogHeading = styled.h3`
  && {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    margin-top: 0px;
    display: flex;
    align-items: center;
  }
`;

export const DialogMessage = styled.div`
  && {
    width: 258px;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    div {
      &.warning {
        font-weight: bold;
        color: ${colors.red};
      }
    }
  }
`;

export const SmallTextField = withStyles(
  (props: TextFieldProps) => <TextField {...props} variant="outlined" />,
  {
    root: {
      boxSizing: 'border-box',
      height: '40px',
      width: '116px',
      '& .MuiOutlinedInput-root': {
        height: '100%',
      },
      '& .MuiOutlinedInput-input': {
        padding: '12px 13px',
        fontSize: 14,
        lineHeight: 20,
      },
    },
  }
);

export const LargeTextField = styled(SmallTextField)`
  && {
    width: 100%;
  }
`;

export const SummaryCalculationsDialogActions = styled(
  (props: DialogActionsProps) => (
    <DialogActions {...props}>{props.children}</DialogActions>
  )
)`
  && {
    margin-top: 8px;
    padding-left: 0px;
    justify-content: flex-start;
  }
`;

export const TotalAvailableUSFDialog = withStyles(
  (props: DialogProps) => <Dialog {...props} disableScrollLock />,
  {
    root: {
      '& .MuiPaper-root': {
        width: 369,
      },
    },
  }
);

export const TotalAvailableUSFDialogContent = styled(DialogContent)`
  && {
    padding: 16px 24px 8px 24px !important;
  }
`;

export const TotalAvailableUsfParagraph = styled.div`
  && {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 18px;
  }
`;

export const CustomSelect = withStyles(
  (props: SelectProps) => {
    return (
      <Select
        variant="outlined"
        IconComponent={KeyboardArrowDownIcon}
        {...props}
        MenuProps={{ disableScrollLock: true }}
      />
    );
  },
  {
    root: {
      zIndex: 0,
    },
    select: {
      boxSizing: 'border-box',
      height: '40px',
      fontSize: '14px !important',
      background: 'white',
      padding: '9px 15px 8px 13px !important',
      borderRadius: '4px',
      border: `1px solid ${colors.selectBorder}`,
    },
  }
);

export const ProgramSummaryDialogTitle = withStyles(DialogTitle, {
  root: {
    padding: '16px 16px 8px 16px',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '12px',
    textTransform: 'uppercase',
    color: '#4A4A4A',
    cursor: 'move',
  },
});

export const ProgramSummaryDialogContent = withStyles(DialogContent, {
  root: {
    padding: '0px 16px 16px',
  },
});

type ProgramSummaryCustomDialogProps = DialogProps & {
  PaperProps?: Partial<PaperComponentProps> & Record<string, unknown>;
  anchorRef?: RefObject<HTMLElement>;
  getTopLeft?: GetTopLeft;
};

const MAXIMUM_HEIGHT = 566;

const INVISIBILITY_STYLES = {
  top: 0,
  left: 0,
};

const PositionableProgramsummaryDialogPaper = styled(Paper)`
  z-index: ${Z_INDEX_HIGHER_THAN_ALL_THE_LAYOUT};
`;

const PositionableProgramsummaryDialog = ({
  anchorRef,
  getTopLeft,
  PaperProps,
  open,
  ...props
}: ProgramSummaryCustomDialogProps) => {
  const [maxHeight, setMaxHeight] = useState<number>();
  const dialogRef = useRef<HTMLDivElement>();
  const dispatch = useDispatch();
  const calculatedStyle = usePositionableDialog(getTopLeft, anchorRef);

  useEffect(() => {
    if (open) dispatch(increasePositionablePSDialogCounter());

    return () => {
      if (open) dispatch(decreasePositionablePSDialogCounter());
    };
  }, [dispatch, open]);

  useEffect(() => {
    if (dialogRef.current) {
      let mh;
      if (calculatedStyle.top + MAXIMUM_HEIGHT <= window.innerHeight) {
        mh = calculatedStyle.top + MAXIMUM_HEIGHT;
      } else {
        mh = window.innerHeight - calculatedStyle.top;
      }

      if (mh > MAXIMUM_HEIGHT) {
        mh = MAXIMUM_HEIGHT;
      }

      setMaxHeight(mh);
    }
  }, [calculatedStyle.top]);

  return (
    <Dialog
      {...props}
      className="positionable-ps-dialog"
      ref={dialogRef}
      open={open}
      PaperComponent={PositionableProgramsummaryDialogPaper}
      PaperProps={{
        ...PaperProps,
        style: maxHeight
          ? {
              ...calculatedStyle,
              maxHeight,
            }
          : INVISIBILITY_STYLES,
      }}
      disableAutoFocus
      disableScrollLock
    />
  );
};

export const ProgramSummaryLargeDialog = withStyles(
  PositionableProgramsummaryDialog,
  {
    root: {
      '& .MuiPaper-root': {
        width: 476,
      },
    },
  }
);

export const useStylesProgramSummary = makeStyles({
  closeButton: {
    color: colors.mainBlue,
  },
});

export const ProgramSummaryDialogBox = styled(Box)`
  && {
    height: 48.93px;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    letter-spacing: 0.25px;
    margin-bottom: 8.16px;
    &.center {
      margin-left: 8px;
      margin-right: 8px;
    }
    &.selected {
      background: #eeeeee;
    }
    &.selectable {
      cursor: pointer;
    }
  }
`;

export const SpaceCategoryDialogBox = styled(Box)`
  && {
    height: 32px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    box-sizing: border-box;
    border-radius: 3px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    font-size: 12px;
    line-height: 16px;
    &.selected {
      background: #eeeeee;
    }
    &.selectable {
      cursor: pointer;
    }
  }
`;

export const SpaceCategoryLabel = styled.span`
  margin-left: 8px;
`;

export const DataPointsCellHeader = styled(TableCell)`
  && {
    padding: 24px 0px 4px 0px;
    font-size: 10px;
    line-height: 12px;
    letter-spacing: 0.25px;
    text-transform: uppercase;
  }
`;

export const DataPointsCellSubHeader = styled(TableCell)`
  && {
    padding: 8px 0px 8px 0px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
  }
`;

export const DataPointsCell = styled(TableCell)`
  && {
    padding: 8px 0px;
    vertical-align: text-top;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
  }
`;

export const ScenarioName = styled.div`
  && {
    font-size: 14px;
    line-height: 24px;
  }
`;

export const DepartmentName = styled.div`
  && {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const DeptSecondaryIdentifier = styled.div`
  && {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    color: #4a4a4a;
  }
`;

export const ProgramSummaryCustomDialog = withStyles(
  PositionableProgramsummaryDialog,
  {
    root: {
      '& .MuiPaper-root': {
        minWidth: 360,
      },
    },
  }
);

export const DeltaValue = styled.div`
  && {
    &.positive {
      color: ${colors.lightGreen};
    }
    &.negative {
      color: ${colors.red};
    }
  }
`;

export const FileCabinetsLockersRatioDialog = withStyles(
  (props: DialogProps) => <Dialog {...props} disableScrollLock />,
  () => {
    return {
      root: {
        '& .MuiPaper-root': {
          minWidth: '630px',
        },
      },
    };
  }
);

export const DialogTable = styled(Table)`
  thead {
    .scenario-name-row th {
      padding: 12px 0 0;
      border-bottom: 0;
    }

    .department-name-row {
      padding-bottom: 4px;

      th {
        padding-top: 4px;
      }
    }
  }
`;
